/* eslint-disable spaced-comment */
import React, { useCallback, useContext, useEffect, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import * as R from 'ramda';
import { arrayOf, bool, func, number, shape, string } from 'prop-types';
import { add, addDays, formatISO, isAfter, isBefore, parseISO, startOfDay } from 'date-fns';
import { useUserRolePermissions } from 'hooks';
import { AuthContext } from 'authentication/AuthContext';
import { GT_ZERO, INTEGER, REQUIRED } from 'utils/helpers/validationMessages';
import { isNumberWithOptionalFormatting } from 'utils/helpers/formValidations';
import { formatDateForExport, formatNumberWithoutDecimals } from 'utils/helpers/format';
import { DataGridPremium, GridCellModes, useGridApiRef } from '@mui/x-data-grid-premium';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Card, CardActions, CardContent, Snackbar, Tooltip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { isEditableStatus, shipmentStatusTooltips } from './utils';
import { useGetBookingOriginsQuery, useGetShippersQuery } from './batchesEndpoints';
import StatusChip from './components/StatusChip';

export const DISTRIBUTIONS_DRAWER_MODE = { EDIT: true, VIEW: false };

const AssociatedShipmentsGrid = ({
  availableDate,
  dataCompanyId,
  isEditPermission,
  onCancel,
  originId,
  onEditPage,
  associatedShipments,
  setAssociatedShipments,
  setUpdatedRows,
  setSelectedShipment,
  setInvalidAgreementCode,
  setInvalidShipmentVolume,
}) => {
  const navigate = useNavigate();
  const { isProductShippingManage } = useUserRolePermissions();
  const { affiliates, currentAffiliate } = useContext(AuthContext);
  const apiRef = useGridApiRef();

  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [editedRow, setEditedRow] = useState({});
  const [cellModesModel, setCellModesModel] = React.useState({});

  const getShipperQuery = { params: { dataCompanyId, originId } };
  const {
    data: shippers = [],
    isFetching: isFetchingShippers,
    isSuccess: isSuccessShippers,
  } = useGetShippersQuery(getShipperQuery);

  const {
    data: bookingOriginsQuery = [],
    isFetching: isFetchingBookingOrigins,
    isSuccess: isSuccessBookingOrigins,
  } = useGetBookingOriginsQuery({
    params: {
      inventoryOwner: {
        companyCustomerIdName: currentAffiliate?.customerIdName,
        dataCompanyId: currentAffiliate?.dataCompanyId,
      },
      originId,
    },
  });

  const validStartDate = parseISO(availableDate);

  const validateAvailableDate = useCallback(
    date => {
      if (R.isNil(date)) {
        return 'Date required';
      }
      if (date.toString() === 'Invalid Date') {
        return 'Invalid Date';
      }
      if (isBefore(new Date(date), new Date(date))) {
        return 'Cannot be in the past';
      }
      if (isAfter(startOfDay(parseISO(date)), addDays(validStartDate, 30))) {
        return 'Cannot be more than 30 days out';
      }
      return '';
    },
    [validStartDate]
  );

  const [validationMessages, setValidationMessages] = useState({
    agreementCode: '',
    requestedDate: {},
    volume: '',
  });

  const validationMessagesProp = R.prop(R.__, validationMessages);

  const validateAgreementCode = value => {
    return value.length > 20 ? 'Agreement Code must be 20 characters or less.' : '';
  };

  const validateVolume = R.cond([
    [R.isEmpty(R.__), R.always(REQUIRED)],
    [R.gt(1), R.always(GT_ZERO)],
    [value => !isNumberWithOptionalFormatting(value), R.always(INTEGER)],
    [R.T, R.always('')],
  ]);

  const hasEditPermissions = R.all(R.equals(true), [onEditPage, isEditPermission]);

  // TODO: This possibly causes an infinite loop
  useEffect(() => {
    R.forEach(row => {
      if (R.prop('requestedDate', row)) {
        const validationMessage = validateAvailableDate(R.prop('requestedDate', row));
        if (!R.isEmpty(validationMessage)) {
          setValidationMessages(
            R.mergeDeepLeft({
              requestedDate: { [R.prop('shipmentId', row)]: validationMessage },
            })
          );
        }
      }
    }, associatedShipments);
  }, [associatedShipments, validateAvailableDate]);
  const getShipper = shipperName => {
    return shippers.find(shipperInfo => shipperInfo.shipperName === shipperName);
  };

  const handleUpdatedRowsChange = shipment => {
    setUpdatedRows(prev => {
      if (!prev.includes(shipment?.shipmentId) && !shipment?.isNew)
        return [...prev, shipment?.shipmentId];
      return prev;
    });
  };

  const updateRow = (shipmentId, label, value) => {
    setAssociatedShipments(
      R.map(
        R.ifElse(
          R.propEq('shipmentId', shipmentId),
          R.compose(R.tap(handleUpdatedRowsChange), R.tap(setEditedRow), R.assoc(label, value)),
          R.identity
        )
      )
    );
    apiRef.current.updateRows([{ [label]: value, shipmentId: Number(shipmentId) }]);
  };

  const handleShipperChange = (shipmentId, { shipperName, shipperIdName }) => {
    updateRow(shipmentId, 'shipperName', shipperName);
    updateRow(shipmentId, 'shipper', { shipperIdName, shipperName });
  };

  const handleRequestedDateChange = (shipmentId, date) => {
    const validationMessage = validateAvailableDate(date);
    setValidationMessages(R.mergeDeepLeft({ requestedDate: { [shipmentId]: validationMessage } }));

    if (R.isEmpty(validationMessage)) {
      updateRow(shipmentId, 'requestedDate', formatISO(date));
    }
  };

  const volumeLessThanDistributions = R.gt(editedRow?.totalDistributionVolume, editedRow?.volume);
  useEffect(() => {
    setInvalidShipmentVolume(volumeLessThanDistributions);
  }, [setInvalidShipmentVolume, volumeLessThanDistributions]);

  const handleVolumeChange = (shipmentId, value) => {
    const validationMessage = validateVolume(value);
    setValidationMessages(R.mergeDeepLeft({ volume: { [shipmentId]: validationMessage } }));
    if (R.isEmpty(validationMessage)) {
      updateRow(shipmentId, 'volume', parseInt(value));
    }
  };

  const handleAgreementCodeChange = (shipmentId, value) => {
    const validationMessage = validateAgreementCode(value);
    setValidationMessages(R.mergeDeepLeft({ agreementCode: { [shipmentId]: validationMessage } }));
    if (R.isEmpty(validationMessage)) {
      setInvalidAgreementCode(false);
      updateRow(shipmentId, 'agreementCode', value);
    } else {
      setInvalidAgreementCode(true);
    }
  };

  const handleBookingOriginChange = (
    shipmentId,
    { bookingOriginName, bookingOriginId, defaultLocation }
  ) => {
    updateRow(shipmentId, 'bookingOriginName', bookingOriginName);
    updateRow(shipmentId, 'bookingOriginId', bookingOriginId);
    defaultLocation && updateRow(shipmentId, 'locationId', defaultLocation);
  };

  const [isOnlyShipment] = useState(false);
  const minWidth = 150;

  const isBatchOwner = shipment =>
    affiliates.some(({ customerNameShort }) =>
      R.equals(customerNameShort, shipment?.batchOwnerName)
    );
  const isShipper = shipment =>
    affiliates.some(({ customerNameShort }) => R.equals(customerNameShort, shipment?.shipperName));

  const isBatchOwnerAndManager = shipment => R.and(isBatchOwner(shipment), isProductShippingManage);
  const isShipperAndManager = shipment => R.and(isShipper(shipment), isProductShippingManage);

  const canCancel = shipment =>
    R.and(
      R.and(isBatchOwnerAndManager(shipment), hasEditPermissions),
      isEditableStatus(R.prop('status', shipment))
    );

  const handleRemoveRow = shipmentId => {
    setAssociatedShipments(prev => R.reject(R.propEq('shipmentId', shipmentId), prev));
  };

  const processRowUpdate = (_, updatedRow) => {
    return updatedRow;
  };

  const editInputStyle = {
    '& fieldset': { border: 'none' },
    '& input': {
      p: '5px',
    },
    '&.Mui-error': {
      backgroundColor: 'error.light',
      color: 'error.dark',
    },

    backgroundColor: 'secondary.light',
    height: '100%',
    width: '100%',
  };

  const columns = [
    {
      cellClassName: 'css-heap-redact',
      field: 'shipmentId',
      headerName: 'Shipment Id',
      minWidth: 100,
      renderCell: ({ row }) =>
        R.prop('isNew', row) ? (
          ''
        ) : (
          <Typography
            className="heap-shipment-id-link"
            onClick={() => navigate(`../batches/shipment/${R.prop('shipmentId', row)}/edit`)}
            sx={{
              color: 'green',
              cursor: 'pointer',
              fontSize: '14px',
              textDecoration: 'underline',
            }}
          >
            {R.prop('shipmentId', row)}
          </Typography>
        ),
    },
    {
      cellClassName: ({ row }) =>
        R.prop('isNew', row) ? 'css-heap-redact highlighter' : 'css-heap-redact',
      editable: hasEditPermissions,
      field: 'shipperName',
      headerName: 'Shipper',
      minWidth,
      renderCell: ({ row }) => R.prop('shipperName', row),
      renderEditCell: ({ row }) => (
        <Autocomplete
          disableClearable
          disabled={!isSuccessShippers}
          getOptionLabel={option => R.prop('shipperName', option)}
          isOptionEqualToValue={R.eqProps('shipperIdName')}
          loading={isFetchingShippers}
          onChange={(_, value) => handleShipperChange(row.shipmentId, value)}
          options={shippers}
          renderInput={inputParams => <TextField {...inputParams} />}
          sx={{ ...editInputStyle }}
          value={getShipper(row.shipperName)}
        />
      ),
    },
    {
      cellClassName: 'css-heap-redact',
      field: 'status',
      headerName: 'Status',
      minWidth: 100,
      renderCell: ({ row }) =>
        R.prop('status', row) && (
          <Tooltip placement="top-start" title={shipmentStatusTooltips[R.prop('status', row)]}>
            <StatusChip status={R.prop('status', row)} />
          </Tooltip>
        ),
    },
    {
      cellClassName: ({ row }) =>
        R.prop('isNew', row)
          ? 'css-heap-redact highlighter'
          : R.prop('shipmentId', row) && isEditableStatus(row.status)
          ? 'css-heap-redact editable-fields'
          : 'css-heap-redact',
      editable: hasEditPermissions,
      field: 'bookingOriginName',
      headerName: 'Booking Origin',
      minWidth,
      renderCell: ({ row }) => R.prop('bookingOriginName', row),
      renderEditCell: ({ row }) => (
        <Autocomplete
          disableClearable
          disabled={!isSuccessBookingOrigins}
          getOptionLabel={option => R.prop('bookingOriginName', option)}
          isOptionEqualToValue={R.eqProps('bookingOriginId')}
          loading={isFetchingBookingOrigins}
          onChange={(_, value) => handleBookingOriginChange(row.shipmentId, value)}
          options={bookingOriginsQuery}
          renderInput={inputParams => <TextField {...inputParams} />}
          sx={{ ...editInputStyle }}
        />
      ),
    },
    {
      cellClassName: ({ row }) =>
        R.prop('isNew', row)
          ? 'highlighter'
          : R.gt(row?.totalDistributionVolume, row?.volume)
          ? 'volume-error'
          : hasEditPermissions && isEditableStatus(row.status)
          ? 'editable-fields'
          : '',
      editable: hasEditPermissions,
      field: 'volume',
      headerName: 'Volume (bbls)',
      minWidth: 150,
      renderEditCell: ({ row }) => (
        <TextField
          error={R.not(R.isEmpty(validationMessagesProp('volume')[row.shipmentId]))}
          helperText={validationMessagesProp('volume')[row.shipmentId]}
          onChange={event =>
            handleVolumeChange(R.prop('shipmentId', row), R.path(['target', 'value'], event))
          }
          placeholder={row.volume}
          slotProps={{
            input: {
              endAdornment: <InputAdornment position="end">bbl</InputAdornment>,
            },
          }}
          sx={{ ...editInputStyle }}
          type="number"
        />
      ),
      type: 'number',
    },
    {
      cellClassName: `css-heap-redact`,
      field: 'bookedVolume',
      headerName: 'Booked Vol. (bbls)',
      minWidth: 150,
      renderCell: ({ row }) => (
        <Box sx={{ textAlign: 'right', width: '100%' }}>
          {R.has('bookedVolume', row) &&
            (R.equals(R.prop('bookedVolume', row), 0)
              ? '--'
              : formatNumberWithoutDecimals(R.prop('bookedVolume', row)))}
        </Box>
      ),

      type: number,
    },
    {
      cellClassName: ({ row }) =>
        R.prop('isNew', row)
          ? 'css-heap-redact highlighter'
          : R.prop('shipmentId', row) && hasEditPermissions && isEditableStatus(row.status)
          ? 'css-heap-redact editable-fields'
          : 'css-heap-redact',
      editable: hasEditPermissions,
      field: 'agreementCode',
      headerName: 'Agreement Code',
      minWidth,
      renderEditCell: ({ row }) => (
        <TextField
          error={R.not(R.isEmpty(validationMessagesProp('agreementCode')[row.shipmentId]))}
          helperText={validationMessagesProp('agreementCode')[row.shipmentId]}
          maxLength={20}
          onChange={event =>
            handleAgreementCodeChange(R.prop('shipmentId', row), R.path(['target', 'value'], event))
          }
          placeholder={R.prop('agreementCode', row)}
          sx={{ ...editInputStyle }}
        />
      ),
    },
    {
      cellClassName: ({ row }) => {
        if (!row || typeof row.shipmentId === 'undefined') {
          return 'css-heap-redact';
        }

        return !R.prop('isNew', row)
          ? 'css-heap-redact highlighter'
          : R.prop('shipmentId', row) && hasEditPermissions && isEditableStatus(row.status)
          ? 'css-heap-redact editable-fields'
          : 'css-heap-redact';
      },
      editable: hasEditPermissions,
      field: 'requestedDate',
      headerName: 'Requested Date',
      minWidth,
      renderEditCell: ({ row }) => {
        if (row.isNew) {
          return null;
        }
        return (
          <DatePicker
            disabled={row.isNew}
            disableMaskedInput
            inputFormat="MM dd, yyyy"
            maxDate={add(validStartDate, { days: 30 })}
            minDate={validStartDate}
            onAccept={() => setIsDatePickerOpen(false)}
            onChange={date => handleRequestedDateChange(R.prop('shipmentId', row), date)}
            onClose={() => setIsDatePickerOpen(false)}
            open={isDatePickerOpen}
            openTo="day"
            slotProps={{
              textField: {
                error: R.not(R.isEmpty(validationMessages.requestedDate[row.shipmentId])),
                helperText: validationMessages.requestedDate[row.shipmentId],
                onClick: () => setIsDatePickerOpen(true),
              },
            }}
            sx={{ ...editInputStyle }}
            value={parseISO(row.requestedDate)}
          />
        );
      },
      valueFormatter: formatDateForExport('MM/d/yyyy'),
    },
    {
      cellClassName: 'css-heap-redact',
      field: 'cancelShipment',
      headerName: 'Cancel Shipment',
      minWidth,
      renderCell: ({ row }) =>
        (canCancel(row) || R.prop('isNew', row)) && (
          <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            <Button
              disabled={!hasEditPermissions}
              onClick={() => {
                R.prop('isNew', row)
                  ? handleRemoveRow(R.prop('shipmentId', row))
                  : onCancel('shipment', R.prop('shipmentId', row), isOnlyShipment);
              }}
              variant="outlined"
            >
              Cancel
            </Button>
          </Box>
        ),
      width: 150,
    },
    {
      cellClassName: 'css-heap-redact',
      field: 'actions',
      headerName: 'Edit Distribution(s)',
      minWidth: 150,
      renderCell: ({ row }) =>
        // eslint-disable-next-line no-restricted-globals
        !R.prop('isNew', row) &&
        R.prop('shipmentId', row) &&
        (R.and(
          R.or(isBatchOwnerAndManager(row), isShipperAndManager(row)),
          isEditableStatus(R.prop('status', row))
        ) ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            <Button
              disabled={!isShipperAndManager(row) || !hasEditPermissions}
              onClick={() => {
                const selectedShipment = {
                  mode: isEditableStatus(row.status)
                    ? DISTRIBUTIONS_DRAWER_MODE.EDIT
                    : DISTRIBUTIONS_DRAWER_MODE.VIEW,
                  shipmentId: R.prop('shipmentId', row),
                };
                // using spread operator to force drawer to refetch data
                setSelectedShipment({ ...selectedShipment });
              }}
              variant="outlined"
            >
              Edit
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            <Button
              disabled={!isShipper}
              onClick={() => {
                setSelectedShipment({
                  mode: DISTRIBUTIONS_DRAWER_MODE.VIEW,
                  shipmentId: R.prop('shipmentId', row),
                });
              }}
              variant="outlined"
            >
              View
            </Button>
          </Box>
        )),

      width: 150,
    },
  ];

  const handleCellClick = useCallback((params, event) => {
    if (!params.isEditable) {
      return;
    }

    // Ignore portal
    if (event.target.nodeType === 1 && !event.currentTarget.contains(event.target)) {
      return;
    }

    setCellModesModel(prevModel => {
      return {
        // Revert the mode of the other cells from other rows
        ...Object.keys(prevModel).reduce(
          (acc, id) => ({
            ...acc,
            [id]: Object.keys(prevModel[id]).reduce(
              (acc2, field) => ({
                ...acc2,
                [field]: { mode: GridCellModes.View },
              }),
              {}
            ),
          }),
          {}
        ),
        [params.id]: {
          // Revert the mode of other cells in the same row
          ...Object.keys(prevModel[params.id] || {}).reduce(
            (acc, field) => ({ ...acc, [field]: { mode: GridCellModes.View } }),
            {}
          ),
          [params.field]: { mode: GridCellModes.Edit },
        },
      };
    });
  }, []);

  const handleCellModesModelChange = useCallback(newModel => {
    setCellModesModel(newModel);
  }, []);

  const volumeSum = {
    apply: () => {
      // Create a lookup of visible shipment IDs and their values
      const visibleShipments = new Map();

      // Add all shipments to the map
      associatedShipments.forEach(shipment => {
        visibleShipments.set(shipment.shipmentId, shipment);
      });

      // Calculate the total for non-canceled visible shipments
      let total = 0;
      visibleShipments.forEach(shipment => {
        if (shipment.status !== 'Canceled' && shipment.status !== 'CANCELED') {
          const volume =
            typeof shipment.volume === 'number' ? shipment.volume : Number(shipment.volume || 0);
          total += Number.isNaN(volume) ? 0 : volume;
        }
      });

      return total;
    },
    label: 'Volume Sum',
    valueFormatter: params => {
      return params && typeof params.value === 'number'
        ? formatNumberWithoutDecimals(params.value)
        : '0';
    },
  };

  return (
    <>
      <Box sx={{ display: 'block', mb: 9, overflow: 'visible', position: 'relative' }}>
        <DataGridPremium
          aggregationFunctions={{ volumeSum }}
          apiRef={apiRef}
          cellModesModel={cellModesModel}
          cellSelection
          columns={columns}
          getRowId={R.prop('shipmentId')}
          hideFooter
          id="shipmentId"
          initialState={{
            aggregation: {
              model: {
                volume: 'volumeSum',
              },
            },
          }}
          isCellEditable={({ row, field }) => {
            if (R.prop('isNew', row)) return true;
            if (!isEditableStatus(row?.status)) return false;

            // making only volume field of edited row editable when volume is less than total volume of distributions
            if (volumeLessThanDistributions && !R.equals(row, editedRow)) return false;
            if (
              volumeLessThanDistributions &&
              R.equals(row, editedRow) &&
              !R.equals(field, 'volume')
            )
              return false;

            if (isBatchOwnerAndManager(row) && isShipperAndManager(row)) {
              return ['volume', 'requestedDate', 'bookingOriginName', 'agreementCode'].includes(
                field
              );
            }
            if (isBatchOwnerAndManager(row)) {
              return ['volume', 'requestedDate', 'agreementCode'].includes(field);
            }
            if (isShipperAndManager(row)) {
              return ['bookingOriginName'].includes(field);
            }
            return false;
          }}
          onCellClick={handleCellClick}
          onCellModesModelChange={handleCellModesModelChange}
          processRowUpdate={processRowUpdate}
          rowCount={associatedShipments?.length || 0}
          rows={associatedShipments}
        />
      </Box>
      <Snackbar open={volumeLessThanDistributions}>
        <Card
          sx={{
            backgroundColor: 'error.dark',
            display: 'flex',
            flexDirection: 'column',
            height: '250px',
            justifyContent: 'center',
            whiteSpace: 'pre-line',
            width: '250px',
          }}
        >
          <CardContent>
            <Typography color="white" variant="body1">
              The shipment volume is less than the total volume of your distributions. Please update
              your distributions before reducing the shipment volume.
            </Typography>
          </CardContent>
          <CardActions sx={{ color: 'white', justifyContent: 'center' }}>
            <Button
              color="inherit"
              onClick={() => {
                setSelectedShipment({
                  mode: DISTRIBUTIONS_DRAWER_MODE.EDIT,
                  shipmentId: R.prop('shipmentId', editedRow),
                });
              }}
              size="small"
              sx={{
                '&:hover': {
                  backgroundColor: 'error.dark',
                  borderColor: 'error.dark',
                },
                backgroundColor: 'error.dark',
                borderColor: 'error.dark',
                color: 'white',
              }}
              variant="outlined"
            >
              EDIT DISTRIBUTIONS
            </Button>
          </CardActions>
        </Card>
      </Snackbar>
    </>
  );
};

AssociatedShipmentsGrid.propTypes = {
  associatedShipments: arrayOf(shape({})).isRequired,
  availableDate: string.isRequired,
  batchOwnerName: string.isRequired,
  dataCompanyId: number.isRequired,
  isEditPermission: bool.isRequired,
  onCancel: func.isRequired,
  onEditPage: bool.isRequired,
  originId: number.isRequired,
  originName: string.isRequired,
  selectedShipment: shape({}),
  setAssociatedShipments: func.isRequired,
  setInvalidAgreementCode: func.isRequired,
  setInvalidShipmentVolume: func.isRequired,
  setSelectedShipment: func.isRequired,
  setUpdatedRows: func.isRequired,
  shipments: arrayOf(shape({})).isRequired,
};

AssociatedShipmentsGrid.defaultProps = {
  selectedShipment: null,
};

export default AssociatedShipmentsGrid;
